// Os critérios de aceite de um Item (ADR-0003): a lista ordenada de passos que
// o agente marca um a um e de que o veredito é derivado.
//
// POR QUE UM COMPONENTE PRÓPRIO, e não mais um bloco em `RegrasDoItem`: as cinco
// facetas são cinco campos de texto independentes, e cada um comita sozinho. Os
// passos são UMA sequência — acrescentar, remover ou reordenar muda a lista
// inteira, e a op `set_steps` substitui tudo de uma vez. Misturar as duas formas
// de edição no mesmo componente faria o estado local de um vazar no do outro.
//
// A EDIÇÃO É OTIMISTA COM COMMIT EXPLÍCITO. O professor digita livremente e a
// lista só sobe quando ele sai do campo ou clica em adicionar/remover: um turno
// por tecla digitada gastaria uma revisão do Rascunho por caractere.
// `React` explícito porque é a convenção deste projeto (RegrasDoItem e
// CampoEditavel fazem igual) e porque o ESLint aqui roda com
// `react/react-in-jsx-scope` ligado: sem o import, CADA tag JSX vira erro e o
// dev server para de compilar. Só o typecheck não pega — o `tsc` aceita o JSX
// automático, o lint do CRA não.
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import { InterviewStep } from "../../types/Interview";
import palette from "../../common/styles/palette";

interface Props {
  passos: (InterviewStep | string)[];
  busy?: boolean;
  cameraFocal?: boolean;
  /** Recebe a lista INTEIRA, já renumerada. Vira uma op `set_steps`. */
  onComitar: (passos: InterviewStep[]) => void;
}

/** Normaliza o que vem do servidor: ele pode mandar string solta. */
const normalizar = (passos: (InterviewStep | string)[]): InterviewStep[] =>
  passos.map((p) =>
    typeof p === "string"
      ? { text: p, order: null, required: true }
      : {
          text: p.text ?? "",
          order: p.order ?? null,
          required: p.required !== false,
        }
  );

/**
 * Renumera `order` sequencialmente quando a lista É ordenada.
 *
 * Ordem pela metade é recusada pelo servidor (`DetectionGrader::partialOrder`),
 * então a UI nunca a produz: se QUALQUER passo tem ordem, todos recebem; se
 * nenhum tem, ninguém recebe. Isso mantém a regra de "ordene todos ou nenhum"
 * como consequência da estrutura, não como aviso que o professor precisa ler.
 */
const renumerar = (passos: InterviewStep[]): InterviewStep[] => {
  const ordenada = passos.some((p) => typeof p.order === "number");
  return passos.map((p, i) => ({
    text: p.text,
    order: ordenada ? i + 1 : null,
    required: p.required !== false,
  }));
};

export default function PassosDoItem({ passos, busy, onComitar, cameraFocal = false }: Props) {
  const [locais, setLocais] = useState<InterviewStep[]>(() =>
    normalizar(passos)
  );

  // Ressincroniza quando o Rascunho muda POR FORA — adoção de Emenda, outra
  // aba, turno do modelo.
  //
  // A assinatura serializada é calculada FORA do efeito e entra como dependência
  // única. Passar `passos` direto dispararia a cada render do pai (array novo,
  // mesmo conteúdo) e apagaria o que o professor está digitando; e um
  // `eslint-disable` para calar o aviso referenciava uma regra que esta config
  // não carrega, o que é erro de lint por si só.
  const assinatura = JSON.stringify(normalizar(passos));
  useEffect(() => {
    setLocais(JSON.parse(assinatura) as InterviewStep[]);
  }, [assinatura]);

  const comitar = (proximos: InterviewStep[]) => {
    const limpos = renumerar(proximos.filter((p) => p.text.trim() !== ""));
    onComitar(limpos);
  };

  const ordenada = locais.some((p) => typeof p.order === "number");

  const mover = (de: number, para: number) => {
    if (para < 0 || para >= locais.length) return;
    const copia = [...locais];
    const [item] = copia.splice(de, 1);
    copia.splice(para, 0, item);
    setLocais(copia);
    comitar(copia);
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: palette.textMuted,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 0.5,
        }}
      >
        <ChecklistRtlIcon
          fontSize="small"
          sx={{ color: palette.brand, fontSize: 16 }}
        />
        Etapas obrigatórias {locais.length > 0 && `(${locais.length})`}
        {ordenada && (
          <Typography
            component="span"
            variant="caption"
            sx={{ color: palette.brand, ml: 0.5 }}
          >
            · ordem exigida
          </Typography>
        )}
      </Typography>

      {locais.length === 0 ? (
        <Typography
          variant="caption"
          sx={{ color: palette.textMuted, display: "block", mb: 0.5 }}
        >
          {cameraFocal ? "Sem etapas complementares: use as regras visuais acima." : "Sem etapas: a IA julga o Item pela regra de texto, como antes."}
        </Typography>
      ) : (
        // A CONSEQUÊNCIA na frente do professor, não só na ADR: cada etapa é
        // obrigatória, e faltar uma derruba o Item — é o que distingue esta
        // lista de uma decomposição meramente descritiva.
        <Typography
          variant="caption"
          sx={{ color: palette.textMuted, display: "block", mb: 0.5 }}
        >
          {cameraFocal
            ? "Estas etapas complementam o critério da câmera 1. Não enxergar uma etapa nesta câmera, por si só, não comprova que ela foi omitida."
            : <>Cada etapa é obrigatória: faltar uma derruba este Item para <strong>não fez</strong>.</>}
          {ordenada && (cameraFocal ? " A sequência visual declarada deve ser considerada junto à visão geral." : <> Fora da ordem declarada conta como <strong>fez errado</strong>.</>)}
        </Typography>
      )}

      <Stack spacing={0.75}>
        {locais.map((passo, i) => (
          <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
            {ordenada && (
              <Typography
                variant="caption"
                sx={{
                  minWidth: 18,
                  pt: 1.2,
                  color: palette.brand,
                  fontWeight: 700,
                }}
              >
                {i + 1}.
              </Typography>
            )}
            <TextField
              value={passo.text}
              placeholder="Ex: eleva o joelho a 90° e estende a perna — nos DOIS lados"
              size="small"
              fullWidth
              multiline
              disabled={busy}
              inputProps={{ "aria-label": `etapa ${i + 1}` }}
              onChange={(e) => {
                const copia = [...locais];
                copia[i] = { ...copia[i], text: e.target.value };
                setLocais(copia);
              }}
              onBlur={() => comitar(locais)}
            />
            {ordenada && (
              <>
                <Tooltip title="Subir">
                  <IconButton
                    size="small"
                    disabled={busy || i === 0}
                    aria-label={`subir etapa ${i + 1}`}
                    onClick={() => mover(i, i - 1)}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Descer">
                  <IconButton
                    size="small"
                    disabled={busy || i === locais.length - 1}
                    aria-label={`descer etapa ${i + 1}`}
                    onClick={() => mover(i, i + 1)}
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Remover etapa">
              <IconButton
                size="small"
                disabled={busy}
                aria-label={`remover etapa ${i + 1}`}
                onClick={() => {
                  const copia = locais.filter((_, k) => k !== i);
                  setLocais(copia);
                  comitar(copia);
                }}
              >
                <DeleteOutlineIcon
                  sx={{ fontSize: 16, color: palette.dangerText }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          disabled={busy}
          onClick={() =>
            setLocais([...locais, { text: "", order: null, required: true }])
          }
        >
          Adicionar etapa
        </Button>
        {locais.length > 1 && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => {
              // Liga ou desliga a ordem para a lista TODA, nunca pela metade.
              const proximos = locais.map((p, i) => ({
                ...p,
                order: ordenada ? null : i + 1,
              }));
              setLocais(proximos);
              onComitar(
                proximos
                  .filter((p) => p.text.trim() !== "")
                  .map((p, i) => ({
                    text: p.text,
                    order: ordenada ? null : i + 1,
                    required: p.required !== false,
                  }))
              );
            }}
          >
            {ordenada ? "Ordem não importa" : "Exigir esta ordem"}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
